<template>
<div class="route-stops-wrapper d-flex flex-column" :class="showAllStops ? 'expanded' : 'collapsed'" style="z-index:1041;">

	<!-- Detail Block / Here to allow proper flex behavior on full layout -->
	<route-item-details
		v-if="config.layout === 'desktop'"
		:route="route"
		:routeDetails="true" />


	<div v-if="config.layout === 'mobile'" class="card mx-auto mb-2">
		<div class="card-header leading-none">Next Visit</div>
		<ul v-if="visits_next" class="list-group list-group-flush">
			<route-item-stops-planned 
				:index="visits_next.visit_num"
				:visit="visits_next"
				:open_default="true" /> 
		</ul>

		<div v-else class="px-35 py-25 d-flex align-items-center">
			<template v-if="route.status === 2">
				<i class="fad fa-badge-check text-primary text-2xl mr-2"></i>
				<span class="text-gray-700">This route is complete. Nice job!</span>
			</template>
			<span v-else class="text-gray-700">No visits found for this route.</span>
		</div>
	</div>


	<div class="route-stops-header flex-shrink-1 mx-auto px-35 py-25 bg-gray-100 text-gray-500 border-bottom">
		<div v-if="!showAllStops && config.layout === 'mobile'" @click="toggleAllStops()" class="d-flex cursor-pointer font-weight-bold">
			<span class="flex-grow-1 text-uppercase text-sm">Show All Visits</span>
			<i class="fal fa-angle-double-up ml-2"></i>
		</div>

		<div v-else class="d-flex">
			<div class="flex-grow-1">
				<strong>{{ route.waypoints.length }} VISITS</strong>
				<i v-if="route.can.update" @click="toggleAddStops()" class="fal fa-plus-circle ml-1" v-tooltip.bottom="'Add Visits'" data-test="toggleAddStops"></i>

				<div class="pt-1 text-sm leading-none">
					<strong>{{ routeDuration.hrs }}</strong>h 
					<strong>{{ routeDuration.mins }}</strong>min
					(<strong>{{ routeDistance }}</strong>mi)
				</div>
			</div>

			<i v-if="config.layout === 'mobile'" @click="toggleAllStops()" class="fal fa-angle-double-down ml-2 cursor-pointer"></i>
		</div>

		<div v-show="showAddStops" class="text-right w-100 pt-25 text-transform-none font-weight-normal">
			<div v-if="route.waypoints.length > 24" class="alert alert-danger text-left text-sm px-25 py-2 leading-tight">
				Routes are limited to <strong>25</strong> visits. To add a visit, remove a visit first.
			</div>
			<template v-else>
				<div class="text-left">Add Providers</div>
				<v-select
					multiple
					label="display_name"
					:options="nonRouteProviders"					
					:reduce="display_name => display_name.location_id"
					v-model="added_visits"
					data-test="addPhysician"
				>
					<template v-slot:option="option">
						<div class="font-weight-bold">
							{{ option.display_name }}
						</div>
						<div class="text-sm">{{ option.short_address }}</div>
					</template>								
				</v-select>

				<span class="btn btn-sm btn-ghost text-transform-none mt-2" @click="toggleAddStops()">Cancel</span>
				<span class="btn btn-sm btn-secondary text-transform-none mt-2" @click="addRouteStop()" data-test="saveAddedStops">Add to Route</span>
			</template>
		</div>
	</div>


	<div class="route-stops-list flex-grow-1 mx-auto">
		<ul v-if="showRouteVisits" class="route-stops-planned list-group list-group-flush">
			<li v-if="error" class="alert alert-danger mb-0">
				Uh oh! There's a problem mapping your route.<br>
				<small>Error: {{ error }}</small>
			</li>

			<li class="list-group-item route-terminal d-flex align-items-top text-gray-700">
				<div class="route-icon rounded-circle circle-cropped avatar-xs text-center font-weight-bold text-white mr-2 cursor-pointer">
					<span class="route-icon-label">A</span>
				</div>
				<span v-if="!newRouteStart">
					{{ route.route_start_other }}
					<span class="btn btn-sm btn-ghost-light text-gray-500 ml-1 leading-none show-on-hover"><i class="fal fa-pen" @click="editRouteStart()" data-test="editRouteStartButton"></i></span>
				</span>

				<div v-else class="flex-grow-1 text-right">
					<v-select
						taggable
						placeholder="Pick existing or type in a new location"
						:options="user.locations"
						v-model="newRouteStartObject"
						autocomplete="new-password"
						class="mb-1"
						data-test="editRouteStart"
					>
						<template v-slot:option="option">
							<div class="font-weight-bold">
								{{ option.label }}
							</div>
							<div class="text-sm">{{ option.short_address }}</div>
						</template>
					</v-select>

					<span class="btn btn-sm btn-ghost-secondary mr-1" @click="resetRouteStart()">Cancel</span>
					<span class="btn btn-sm btn-primary" @click="changeRouteStart()" data-test="saveRouteStartButton">Save</span>
				</div>
			</li>


			<route-item-stops-planned 
				v-for="(visit, index) in visits_planned_ordered"
				:index="index"
				:key="index"
				:visit="visit"
				:open_default="false" /> 


			<li class="list-group-item route-terminal d-flex align-items-top text-gray-700">
				<div class="route-icon rounded-circle circle-cropped avatar-xs text-center font-weight-bold text-white mr-2 cursor-pointer">
					<span class="route-icon-label">{{ String.fromCharCode('B'.charCodeAt() + visits_planned_ordered.length) }}</span>
				</div>
				<span v-if="!newRouteEnd">
					{{ route.route_end_other }}
					<span class="btn btn-sm btn-ghost-light text-gray-500 ml-1 leading-none show-on-hover"><i class="fal fa-pen" @click="editRouteEnd()" data-test="editRouteEndButton"></i></span>
				</span>

				<div v-else class="flex-grow-1 text-right">
					<v-select
						taggable
						placeholder="Pick existing or type in a new location"
						:options="user.locations"
						:select-on-key-codes="[9, 13]"
						v-model="newRouteEndObject"
						autocomplete="new-password"
						class="mb-1"
						data-test="editRouteEnd"
					>
						<template v-slot:option="option">
							<div class="font-weight-bold">
								{{ option.label }}
							</div>
							<div class="text-sm">{{ option.short_address }}</div>
						</template>
					</v-select>

					<span class="btn btn-sm btn-ghost-secondary mr-1" @click="resetRouteEnd()">Cancel</span>
					<span class="btn btn-sm btn-primary" @click="changeRouteEnd()" data-test="saveRouteEndButton">Save</span>
				</div>
			</li>
		</ul>

		<template v-if="showExtraCheckins">
			<ul class="route-stops-extra list-group list-group-flush border-bottom" data-test="extraCheckinsList">
				<li v-for="visit, index in visits_extra"
					@click="showCheckin(visit.checkin_id)"
					class="route-stop list-group-item d-flex align-items-center drop-down-hover"
					data-toggle="modal" data-target="#checkinModal">

					<div class="route-icon rounded-circle circle-cropped avatar-xs text-center font-weight-bold text-white mr-2">
						{{ index + 1 }}
					</div>
					<span class="flex-grow-1">{{visit.physician.display_name}}</span>
					<div class="drop-down">
						<a href="#" role="button" id="dropdownMenuLink" class="show-on-hover" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"><i class="far fa-ellipsis-h text-xl"></i></a>
						<div class="dropdown-menu dropdown-menu-right py-0" aria-labelledby="dropdownMenuLink">
							<span
								@click="showCheckin(visit.checkin_id)"
								class="dropdown-item"
								data-toggle="modal" data-target="#checkinModal">View Check-in
							</span>
							<router-link :to="{ name: 'physicians.show', params: { id: visit.physician.id } }" class="dropdown-item" v-on:click.stop>
								View Physician
							</router-link>
						</div>
					</div>
				</li>
			</ul>

			<div v-if="route.can.update" class="bg-white text-center border-top py-25">
				<div class="text-sm">
					Making an unplanned stop?
				</div>
				<span
					@click="$emit('clear_checkin_modal')"
					class="btn btn-ghost-primary cursor-pointer leading-none"
					data-toggle="modal" data-target="#myModal"
					data-test="addExtraCheckin">
				
					Add a Check-In

					<i class="fal fa-plus-circle text-lg"></i>
				</span>
			</div>
		</template>


		<div v-if="config.layout === 'mobile'" class="route-stops-menu navbar bg-white text-center text-sm border-top mx-auto p-0">
			<div @click="toggleRouteVisits()" class="flex-fill btn btn-ghost btn-sm text-gray-700 text-sm" :class="{ 'bg-gray-100' : !showExtraCheckins }">
				<i class="text-secondary fal fa-route pt-1"></i><br>
				Route Visits <span class="badge badge-sm bg-primary text-white" v-html="visits_planned_ordered.length" />
			</div>

			<div @click="toggleExtraCheckins()" class="flex-fill btn btn-ghost btn-sm text-gray-700 text-sm" :class="{ 'bg-gray-100' : showExtraCheckins }">
				<i class="text-secondary fal fa-user pt-1"></i><br>
				Extra Check-ins <span class="badge badge-sm bg-primary text-white" v-html="visits_extra.length" />
			</div>
		</div>		
	</div>


	<div v-if="config.layout === 'desktop'" class="route-stops-menu w-100 navbar flex-shrink-1 bg-white text-center text-sm border-top mx-auto p-0">
		<span @click="toggleRouteVisits()" class="flex-fill btn btn-ghost btn-sm text-gray-700 text-sm" :class="{ 'bg-gray-100' : !showExtraCheckins }" data-test="showPlannedVisits">
			<i class="text-secondary fal fa-route pt-1"></i><br>
			Route Visits <span class="badge badge-sm bg-primary text-white" v-html="visits_planned_ordered.length" data-test="plannedVisitCount" />
		</span>

		<span @click="toggleExtraCheckins()" class="flex-fill btn btn-ghost btn-sm text-gray-700 text-sm" :class="{ 'bg-gray-100' : showExtraCheckins }" data-test="showExtraCheckins">
			<i class="text-secondary fal fa-user pt-1"></i><br>
			Extra Check-ins <span class="badge badge-sm bg-primary text-white" v-html="visits_extra.length" />
		</span>
	</div>

</div>
</template>

<script>
	import { mapGetters, mapState } from 'vuex';
	import RouteItemDetails from './RouteItemDetails';
	import RouteItemStopsPlanned from './RouteItemStopsPlanned';

  	export default {  		
		components: {
			RouteItemDetails,
			RouteItemStopsPlanned,
        },

		props: ['data_loaded', 'route'],

		data () {
			return {
	        	added_visits: [],

	        	measures: {
	        		distance: [],
	        		duration: [],
	        	},
	        	order: [],	

	        	visits_planned_ordered: [],	

	        	showAddStops: false,
	        	showAllStops: false,
				showRouteVisits: true,
				showExtraCheckins: false,
	        	error:false,


	        	newRouteStart: false,
	        	newRouteStartObject: null,

	        	newRouteEnd: false,
	        	newRouteEndObject: null,
			}
		},

		created() {
			//this.$store.dispatch('fetchMappablePhysicianIds');
		    Vue.nextTick(() => {
		    	this.updateProgress(this.routeProgress);
		    });
		},

		mounted() {
		    this.$root.$on('route_changed', measures => {
		    	this.measures = measures;
		    });

			this.$root.$on('route_order', order => {
		    	this.order = order;
		    	this.visits_planned_ordered.length = 0;
		    	this.order.forEach(waypoint => {
		    		this.visits_planned_ordered.push(this.visits_planned[waypoint]);
		    	});
		    });

		    this.$root.$on('route_error', error => {
		    	this.error = error;
		    });
		},

		methods: {
			addRouteStop() {
				axios.post('/api/route-visits/store', { 
					added_visits: this.added_visits,
					type: 'planned',
					route_id: this.route.id,
				}).then(({ data }) => { //returns the route visits
                    this.$root.$emit('route_stops_changed', data);
                    this.added_visits = [];
                })									
			},

			changeRouteEnd() {
				//this.newRouteStartObject is an object if selected; a string if user-entered
				this.$root.$emit('update_terminals', {
					route_end: this.newRouteEndObject.id ? this.newRouteEndObject.id : -1,
					route_end_other: this.newRouteEndObject.label ? this.newRouteEndObject.label : this.newRouteEndObject,
				});
				this.newRouteEnd = false;
			},

			changeRouteStart() {
				this.$root.$emit('update_terminals', {
					route_start: this.newRouteStartObject.id ? this.newRouteStartObject.id : -1,
					route_start_other: this.newRouteStartObject.label ? this.newRouteStartObject.label : this.newRouteStartObject,
				});
				this.newRouteStart = false;
			},

			editRouteEnd() {
				this.newRouteEnd = true;
			},

			editRouteStart() {
				this.newRouteStart = true;
			},

			resetRouteEnd() {
				this.newRouteEnd = false;
			},

			resetRouteStart() {
				this.newRouteStart = false;
			},

			showCheckin(checkin_id) {
				this.$root.$emit('show_checkin', checkin_id);
			},

			toggleAddStops() {
				this.showAddStops = !this.showAddStops;
			},

			toggleAllStops() {
				this.showAllStops = !this.showAllStops;
			},

			toggleExtraCheckins() {
				this.showExtraCheckins = true;
				this.showRouteVisits = false;
			},

			toggleRouteVisits() {
				this.showExtraCheckins = false;
				this.showRouteVisits = true;
			},	

			updateProgress(progress) {
				this.$root.$emit('route_progress_changed', progress);
			},						
		},

		computed: {
			...mapGetters(['mappableProviderIds']),
			...mapState(['config', 'user']),

			routeDistance() {
				//meters to miles with one decimal
				var distanceSum = this.measures.distance.reduce((array, distance) => array + distance, 0);
				return Math.round ( (distanceSum / 1609.34) * 10 ) / 10;
			},

			routeDuration() {
				//seconds to hours
				var durationSum = this.measures.duration.reduce((array, duration) => array + duration, 0);
				var hrs = Math.floor(durationSum / 3600);
            	var mins = Math.floor((durationSum - (hrs * 3600)) / 60);
				return { hrs: hrs, mins: mins }
			},

			user_locations() {
				return [...this.user.locations, { label: 'Other', short_address: 'Enter address' }];
			},

		    visits_extra() {
		    	if (this.data_loaded) {
					return this.route.visits.filter(function(visit) {
						return visit.type == 'extra';
					})
				}
		    },

		    visits_next() {
		    	if (this.visits_planned_ordered) {
		    		let index = this.visits_planned_ordered.findIndex((element) => {
	                    return element.checkin_id === null;
	                });

	                let visit = this.visits_planned_ordered[index];

	                if (visit) {
	                	visit.visit_num = index;
	                }

	                return visit;
		    	}
		    },

		    visits_planned() {
		    	if (this.data_loaded) {
					return this.route.visits.filter(function(visit) {
						return visit.type == 'planned';
					})
				}
		    },

			routeProgress() {
				if (this.data_loaded) {
					var count = 0;
					this.visits_planned.forEach(visit => {
						if(visit.checkin_id) {
							count++;
						}
					});
					var progress = 100*(count / this.visits_planned.length);
					progress = Math.round(progress);

					return progress;
				}
			},

			nonRouteProviders() {
				return this.mappableProviderIds.filter(provider => !this.route.visits.some(visit => visit.location_id == provider.location_id));
			}
		},

		watch: {
			routeProgress() {
				this.updateProgress(this.routeProgress);
				if (this.routeProgress === 100 && this.route.status != 2) {
					this.$root.$emit('route_completed');				
				}
				if (this.routeProgress < 100 && this.route.status == 2) {
					this.$root.$emit('route_extended');				
				}				
			},
		},			
	}
</script>